import React, { Component, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckoutErrorScreen from './CheckoutErrorScreen';

interface LazyLoadErrorBoundaryProps {
  children: ReactNode;
  fallbackPath?: string;
}

interface LazyLoadErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class LazyLoadErrorBoundary extends Component<LazyLoadErrorBoundaryProps, LazyLoadErrorBoundaryState> {
  constructor(props: LazyLoadErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): LazyLoadErrorBoundaryState {
    // Check if this is a module loading error
    const isModuleError = error.message?.includes('Failed to fetch dynamically imported module') ||
                         error.message?.includes('Loading chunk') ||
                         error.message?.includes('Module') ||
                         error.name === 'ChunkLoadError';
    
    console.error('LazyLoadErrorBoundary caught error:', error);
    
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('LazyLoadErrorBoundary componentDidCatch:', error, errorInfo);
    
    // Optional: Send error to monitoring service
    // logErrorToService(error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    // Force a hard reload to clear any cached modules
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // For checkout page specifically, show our custom error screen
      if (window.location.pathname === '/checkout') {
        return <CheckoutErrorScreen error={this.state.error} onRetry={this.reset} />;
      }
      
      // For other pages, show a generic error with navigation
      return <FallbackErrorComponent error={this.state.error} onRetry={this.reset} fallbackPath={this.props.fallbackPath} />;
    }

    return this.props.children;
  }
}

// Functional component for the fallback error UI (for non-checkout pages)
const FallbackErrorComponent: React.FC<{
  error: Error;
  onRetry: () => void;
  fallbackPath?: string;
}> = ({ error, onRetry, fallbackPath = '/' }) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate(fallbackPath);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="max-w-md w-full">
        <div className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Page Loading Error
          </h1>

          <p className="text-gray-600 mb-6 leading-relaxed">
            We're having trouble loading this page. This might be due to a temporary network issue.
          </p>

          {/* Error Details (in development) */}
          {import.meta.env.DEV && error && (
            <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 mb-6 text-left">
              <p className="text-xs text-orange-600 font-mono break-all">
                {error.message}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={onRetry}
              className="w-full bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white font-medium py-3 px-6 rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>

            <button
              onClick={handleGoHome}
              className="w-full border border-jamaica-green/20 text-jamaica-green hover:bg-jamaica-green/5 font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Go Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LazyLoadErrorBoundary;